import {
  Box,
  Checkbox,
  FormControl,
  FormErrorMessage,
  FormHelperText,
  FormLabel,
  Heading,
  HStack,
  Input,
  Spinner,
  Stack,
  Text,
} from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import {
  NegotiationRoundStatus,
  type NegotiationRoundV2Dto,
  type NegotiationRoundV2PatchDto,
} from "../../../../../autogen/bff-api";
import { useEditor } from "../../../../../common/editing/save-coordinator";
import { DateTimeSelector } from "../../../../../common/input/DateTimeSelectors/DateTimeSelector";
import { TipTap } from "../../../../../common/input/TipTap/TipTap";
import { StepNavigation } from "../../../../../common/StepNavigation";
import { isDeadlineAhead } from "./RoundReviewStep";

const TITLE_DEBOUNCE_MS = 300;

interface Props {
  round: NegotiationRoundV2Dto;
  nextStep?: () => void;
  nextStepLabel?: string;
}

export const RoundDetailsStep = ({ round, nextStep, nextStepLabel }: Props) => {
  const { t } = useTranslation();
  const { state, updateField, entityStatus } = useEditor<NegotiationRoundV2Dto, NegotiationRoundV2PatchDto>(
    round.id,
  );
  const deadlineHasPassed =
    round.status === NegotiationRoundStatus.Draft && !!state.deadline && !isDeadlineAhead(state.deadline);

  return (
    <Box pb="8" pt="10">
      <Heading as="h2" size="md" mb="6">
        {t("Details")}
      </Heading>
      <Stack spacing="5" maxW="2xl">
        <FormControl>
          <FormLabel fontSize="sm">{t("Title")}</FormLabel>
          <Input
            value={state.title ?? ""}
            onChange={(e) => updateField({ title: e.target.value || null }, { debounceMs: TITLE_DEBOUNCE_MS })}
            placeholder={t("Negotiation round {{n}}", { n: round.roundNumber }) ?? ""}
            maxW="md"
          />
        </FormControl>

        <FormControl isInvalid={deadlineHasPassed}>
          <FormLabel fontSize="sm">{t("Bid deadline")}</FormLabel>
          <DateTimeSelector
            date={state.deadline}
            onChange={(isoDate) => {
              if (isoDate) updateField({ deadline: isoDate });
            }}
          />
          <FormErrorMessage>
            {t("This deadline has already passed. A round can only be published with a deadline in the future.")}
          </FormErrorMessage>
        </FormControl>

        <FormControl>
          <HStack>
            <Checkbox
              isChecked={state.isFinalRound ?? false}
              onChange={(e) => updateField({ isFinalRound: e.target.checked })}
              colorScheme="teal"
            >
              <Text fontSize="sm">{t("Final Round")}</Text>
            </Checkbox>
          </HStack>
          <FormHelperText>
            {t("No further rounds can be started after a final round. Suppliers see on the round card that it is their last chance to revise their bid.")}
          </FormHelperText>
        </FormControl>

        <FormControl>
          <FormLabel fontSize="sm">{t("Negotiation round description")}</FormLabel>
          <TipTap
            content={state.description ?? ""}
            editable={true}
            onUpdate={(content) => updateField({ description: content })}
          />
        </FormControl>

        {/* Height is reserved: the browser sends no click when mousedown and mouseup land on different elements. */}
        <Box data-testid="round-save-status" aria-busy={entityStatus === "saving"} minH="6">
          {entityStatus === "saving" && <Spinner size="sm" color="smPrimary" />}
        </Box>
      </Stack>
      <StepNavigation onNext={nextStep} nextLabel={nextStepLabel} />
    </Box>
  );
};
